import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClose,
  faSpinnerThird,
  faRotateRight,
  faCheckCircle,
  faPlus,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactDividerModal from 'shared/design-system/components/TrueImpactDividerModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import {
  NPOUser,
  createProgram,
  getAllNPOUsers,
} from '../ReportsBeta/NewProgram/api';

type RowStatus = 'idle' | 'loading' | 'success' | 'error';

interface Row {
  key: number;
  name: string;
  manager: NPOUser | undefined;
  status: RowStatus;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const INITIAL_ROWS = 3;

const makeRow = (key: number): Row => ({
  key,
  name: '',
  manager: undefined,
  status: 'idle',
});

const AddProgramsHeader: React.FC<{ organizationName?: string }> = ({
  organizationName,
}) => (
  <StyledHeader>
    <TrueImpactText variant="heading" className="header-title">
      Add your programs
    </TrueImpactText>
    <TrueImpactText variant="body-small" color="greyDark" className="intro">
      List each core program or initiative{' '}
      <strong>{organizationName || 'your organization'}</strong> runs. You'll
      create a profile for each one before reporting on that program.
    </TrueImpactText>
  </StyledHeader>
);

const AddProgramsFooter: React.FC<{
  submitting: boolean;
  canSubmit: boolean;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: () => void;
}> = ({ submitting, canSubmit, submitLabel, onCancel, onSubmit }) => (
  <>
    <TrueImpactButton
      variant="secondary-large"
      disabled={submitting}
      onClick={onCancel}
    >
      Cancel
    </TrueImpactButton>
    <TrueImpactButton
      variant="primary-large"
      isLoading={submitting}
      disabled={!canSubmit}
      onClick={onSubmit}
    >
      {submitLabel}
    </TrueImpactButton>
  </>
);

const AddProgramsModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const client = useClient();
  const { organizationName, newProgramVisibility } = useUserActions();

  const [allNPOUsers, makeAllNPOUsersRequest] = useGraphQLRequest<{
    allUsers: NPOUser[];
  }>();
  const [rows, setRows] = useState<Row[]>(() =>
    Array.from({ length: INITIAL_ROWS }, (_, i) => makeRow(i)),
  );
  const [submitting, setSubmitting] = useState(false);
  const [nextKey, setNextKey] = useState(INITIAL_ROWS);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (!client || !open) return;
    makeAllNPOUsersRequest(getAllNPOUsers(client));
  }, [client, open, makeAllNPOUsersRequest]);

  const users = useMemo<NPOUser[]>(
    () =>
      allNPOUsers.type === 'REQUEST_SUCCESS'
        ? allNPOUsers.data.allUsers || []
        : [],
    [allNPOUsers],
  );

  useEffect(() => {
    if (!open) {
      setRows(Array.from({ length: INITIAL_ROWS }, (_, i) => makeRow(i)));
      setNextKey(INITIAL_ROWS);
      setSubmitting(false);
      setShowValidation(false);
    }
  }, [open]);

  const updateRow = useCallback((key: number, patch: Partial<Row>) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((key: number) => {
    setRows(prev => prev.filter(r => r.key !== key));
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => [...prev, makeRow(nextKey)]);
    setNextKey(k => k + 1);
  }, [nextKey]);

  const namedRows = useMemo(
    () => rows.filter(r => r.status !== 'success' && r.name.trim().length > 0),
    [rows],
  );
  const rowsMissingManager = useMemo(() => namedRows.filter(r => !r.manager), [
    namedRows,
  ]);
  const pendingRows = useMemo(
    () => namedRows.filter(r => r.manager !== undefined),
    [namedRows],
  );

  const hasFailed = rows.some(r => r.status === 'error');
  const hasMissingManager = rowsMissingManager.length > 0;
  const missingManagerError = showValidation && hasMissingManager;

  const handleSubmit = useCallback(async () => {
    if (!client || submitting || namedRows.length === 0) return;
    if (hasMissingManager) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    setSubmitting(true);

    let allSucceeded = true;
    for (const row of pendingRows) {
      updateRow(row.key, { status: 'loading' });
      try {
        const res = await createProgram(client, {
          logoFileId: null,
          managerId: row.manager!.userId,
          name: row.name.trim(),
          status: 'active',
          visibility: newProgramVisibility,
        });
        const ok = res.data?.newProgram?.success;
        updateRow(row.key, { status: ok ? 'success' : 'error' });
        if (!ok) allSucceeded = false;
      } catch {
        updateRow(row.key, { status: 'error' });
        allSucceeded = false;
      }
    }

    setSubmitting(false);
    onCreated?.();
    if (allSucceeded) onClose();
  }, [
    client,
    submitting,
    namedRows.length,
    pendingRows,
    hasMissingManager,
    updateRow,
    newProgramVisibility,
    onCreated,
    onClose,
  ]);

  const submitLabel = hasFailed ? 'Retry' : 'Create profile tasks';
  // Keep the button enabled when a manager is missing so clicking it can
  // surface the validation message; `handleSubmit` short-circuits in that case.
  const canSubmit = namedRows.length > 0 && !submitting;

  return (
    <TrueImpactDividerModal
      open={open}
      onClose={onClose}
      header={<AddProgramsHeader organizationName={organizationName} />}
      footer={
        <AddProgramsFooter
          submitting={submitting}
          canSubmit={canSubmit}
          submitLabel={submitLabel}
          onCancel={onClose}
          onSubmit={handleSubmit}
        />
      }
    >
      <StyledContent>
        <div className="grid-header">
          <TrueImpactText variant="body-small-bold" color="greyDark">
            PROGRAM NAME
          </TrueImpactText>
          <TrueImpactText variant="body-small-bold" color="greyDark">
            REPORTING MANAGER
          </TrueImpactText>
          <span />
        </div>
        {rows.map(row => {
          const locked = row.status === 'loading' || row.status === 'success';
          return (
            <div key={row.key} className="row">
              <TrueImpactTextfield
                value={row.name}
                placeholder="Program name"
                disabled={locked}
                onChange={value => updateRow(row.key, { name: value })}
              />
              <TrueImpactDropdown
                value={row.manager}
                options={users}
                getId={u => u.userId}
                getLabel={u => `${u.firstName} ${u.lastName}`}
                native={true}
                disabled={locked}
                onChange={u => updateRow(row.key, { manager: u })}
                onClear={undefined}
                getItemDisability={undefined}
                name="Select manager"
              />
              <RowStatusIcon
                status={row.status}
                canRemove={rows.length > 1}
                onRemove={() => removeRow(row.key)}
                onRetry={() => updateRow(row.key, { status: 'idle' })}
              />
            </div>
          );
        })}
        <TrueImpactText
          allowTextSelection={false}
          className="add-another"
          onClick={addRow}
        >
          <FontAwesomeIcon icon={faPlus} /> Add another program
        </TrueImpactText>
        {missingManagerError && (
          <TrueImpactText
            variant="body-small"
            color="critical"
            className="error-text"
          >
            Please select a reporting manager for every program you added.
          </TrueImpactText>
        )}
      </StyledContent>
    </TrueImpactDividerModal>
  );
};

const RowStatusIcon: React.FC<{
  status: RowStatus;
  canRemove: boolean;
  onRemove: () => void;
  onRetry: () => void;
}> = ({ status, canRemove, onRemove, onRetry }) => {
  if (status === 'loading') {
    return (
      <span className="status-icon" aria-label="Creating">
        <FontAwesomeIcon icon={faSpinnerThird} spin color={COLORS.greyDark} />
      </span>
    );
  }
  if (status === 'success') {
    return (
      <span className="status-icon" aria-label="Created">
        <FontAwesomeIcon icon={faCheckCircle} color={COLORS.success} />
      </span>
    );
  }
  if (status === 'error') {
    return (
      <TrueImpactIconButton
        icon={faRotateRight}
        label="Retry"
        color="critical"
        onClick={onRetry}
      />
    );
  }
  return (
    <span className={canRemove ? undefined : 'not-allowed'}>
      <TrueImpactIconButton
        icon={faClose}
        label="Remove"
        color="greyDark"
        disabled={!canRemove}
        onClick={canRemove ? onRemove : undefined}
      />
    </span>
  );
};

const StyledHeader = styled.div`
  display: contents;
  .intro strong {
    color: ${COLORS.blackBase};
  }
`;

const StyledContent = styled.div`
  display: contents;
  .grid-header,
  .row {
    display: grid;
    grid-template-columns: 1fr 1fr 32px;
    align-items: center;
    column-gap: ${SPACERS.small};
  }
  .row > *:last-child,
  .grid-header > *:last-child {
    margin-left: -${SPACERS.tiny};
  }
  .grid-header {
    padding-top: ${SPACERS.small};
  }
  .not-allowed {
    display: inline-flex;
    cursor: not-allowed;
  }
  .not-allowed > * {
    pointer-events: none;
  }
  .status-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 16px;
  }
  .add-another {
    align-self: flex-start;
    cursor: pointer;
    color: ${COLORS.ctaPrimary};
  }
`;

export default AddProgramsModal;
